import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Text } from '@bonusly/design-system';

import type { BizyUiBlockAction, RecognitionViewCard } from '../../../schemas/bizy-ui-blocks';
import { useBizyUiBlockActionDispatcher } from '../lib/ui-block-action-dispatcher';
import { AmountPill, HashtagToken, RecipientHandle } from './lib/recognition-segments';

/**
 * Read-only reference to a real recognition post.
 *
 * Visual shape (matches the "Show recognition" column of the target mock):
 *
 *   Giver Name: @recipient +10
 *   [message body #hashtag]
 *
 *   Apr 16, 2026                     [View post]
 *
 * Unlike the draft card, the full Bonusly post header IS shown here
 * because this is a snapshot of a real bonus that already exists. No
 * divider line — spacing alone separates the body from the date / View
 * post footer (matches `ui-block-action-bar.tsx`).
 */
export function RecognitionViewCardView({ block, fontSize = 'sm' }: { block: RecognitionViewCard; fontSize?: 'sm' | 'md' }) {
	const { giver, recipient, amount, message, hashtags, date, actions } = block.payload;
	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const hashtagList = (hashtags ?? []).map((h) => h.replace(/^#/, ''));
	const hasHeader = !!giver || recipients.length > 0 || (amount != null && amount > 0);
	const hasFooter = !!date || actions.length > 0;

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{/* Header line: Giver Name: @recipient @recipient2 [+10] — uses the same segments as the draft card so view + draft read identically. */}
			{hasHeader && (
				<Text fontSize={fontSize} lineHeight="tall">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{amount != null && amount > 0 && <AmountPill amount={amount} />}
				</Text>
			)}
			<Text fontSize={fontSize} mt={hasHeader ? 1 : 0} whiteSpace="pre-wrap">
				{message}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			{hasFooter && (
				<Flex justify="space-between" align="center" mt={3}>
					<Text fontSize="xs" color="text.softer">
						{date}
					</Text>
					<Flex gap={2} align="center">
						{actions.map((action, index) => (
							<InlineFooterAction key={`${action.type}-${index}`} action={action} />
						))}
					</Flex>
				</Flex>
			)}
		</Box>
	);
}

/**
 * Local single-action button for the footer row. Keeps the recognition
 * view card's footer visually distinct from the draft-card action bar
 * (footer is "metadata + action", not "divider + action row").
 */
function InlineFooterAction({ action }: { action: BizyUiBlockAction }) {
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();
	const label =
		action.label ??
		(action.type === 'view_post'
			? t('uiBlocks.actions.viewPost')
			: action.type === 'open_in_givebox'
				? t('uiBlocks.actions.openInGivebox')
				: action.type === 'dismiss'
					? t('uiBlocks.actions.dismiss')
					: action.type === 'insert_into_draft'
						? t('uiBlocks.actions.insertIntoDraft')
						: t('uiBlocks.actions.sendNow'));
	return (
		<Button variant="ghost" colorScheme="secondary" size="sm" onClick={() => dispatch(action)}>
			{label}
		</Button>
	);
}
